import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaFileAlt, FaTrash, FaUpload } from "react-icons/fa";
import { resolveImageUrl } from "../config/apiConfig";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import {
  deleteTaskFile,
  extractTaskFilesFromPayload,
  fetchTaskFiles,
  filterFilesForTask,
  uploadTaskFile
} from "./taskFileService";
import "../assets/css/Issues.css";
import "../assets/css/ViewCustomer.css";
import "../assets/css/DigixLeads.css";

const TASK_ATTACHMENTS_ROWS_PER_PAGE_KEY = "taskAttachmentsRowsPerPage";

function fixFileUrl(url) {
  if (!url || typeof url !== "string") return url;
  return resolveImageUrl(url);
}

function formatUploadedOn(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return String(value);
  }
}

/**
 * GET /files?task_id= (or taskt_id) + POST /file with task_id
 */
export default function TaskFileAttachments({
  task,
  taskId,
  canUpload = false,
  canDelete,
  onRefresh,
  title = "Task attachments"
}) {
  const allowDelete = canDelete !== undefined ? canDelete : canUpload;

  const resolvedTaskId = useMemo(() => {
    const raw = task?.Task_Id ?? task?.task_id ?? taskId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [task?.Task_Id, task?.task_id, taskId]);

  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [apiFiles, setApiFiles] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [attachmentPage, setAttachmentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(TASK_ATTACHMENTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const inputRef = useRef(null);
  const ATTACHMENT_COL_DEFAULT_WIDTHS = allowDelete ? [340, 190, 170, 100] : [420, 220, 190];
  const { getColumnStyle, handleResizeStart } = useResizableColumns(ATTACHMENT_COL_DEFAULT_WIDTHS);

  const fromPayload = useMemo(() => extractTaskFilesFromPayload(task), [task]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (resolvedTaskId == null) {
        setApiFiles([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setMessage({ type: "", text: "" });
      try {
        const remote = await fetchTaskFiles(resolvedTaskId);
        if (!cancelled) setApiFiles(Array.isArray(remote) ? remote : []);
      } catch (err) {
        console.error("Task attachments load:", err);
        if (!cancelled) {
          setApiFiles([]);
          setMessage({
            type: "err",
            text: err.response?.data?.message || err.response?.data?.error || err.message || "Could not load attachments."
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [resolvedTaskId]);

  const displayFiles = useMemo(() => {
    if (resolvedTaskId == null) return [];
    const merged = [...apiFiles, ...fromPayload];
    const scoped = filterFilesForTask(merged, resolvedTaskId);
    const map = new Map();
    scoped.forEach((f) => {
      const key = f.fileId ?? f.fileUrl ?? f.fileName;
      if (key != null) map.set(String(key), f);
    });
    return [...map.values()];
  }, [fromPayload, apiFiles, resolvedTaskId]);

  const getAttachmentSortValue = (file, columnId) => {
    if (columnId === "fileName") {
      return String(file?.fileName || "").toLowerCase();
    }
    if (columnId === "uploadedOn") {
      const ts = new Date(file?.uploadedOn || "").getTime();
      return Number.isFinite(ts) ? ts : 0;
    }
    return "";
  };

  const sortedFiles = useMemo(() => {
    if (!sortColumn) return displayFiles;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...displayFiles].sort((a, b) => {
      const va = getAttachmentSortValue(a, sortColumn);
      const vb = getAttachmentSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.fileId ?? 0);
      const idb = Number(b?.fileId ?? 0);
      return (ida - idb) * mult;
    });
  }, [displayFiles, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedFiles.length / rowsPerPage));
  const startIdx = (attachmentPage - 1) * rowsPerPage;
  const currentFiles = sortedFiles.slice(startIdx, startIdx + rowsPerPage);

  useEffect(() => {
    if (attachmentPage > totalPages && totalPages > 0) {
      setAttachmentPage(1);
    }
  }, [attachmentPage, totalPages, sortedFiles.length]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setAttachmentPage(1);
    localStorage.setItem(TASK_ATTACHMENTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setAttachmentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || resolvedTaskId == null) return;
    setUploading(true);
    setMessage({ type: "", text: "" });
    try {
      await uploadTaskFile(resolvedTaskId, file);
      setMessage({ type: "ok", text: "File uploaded successfully." });
      if (onRefresh) await onRefresh();
      const remote = await fetchTaskFiles(resolvedTaskId);
      setApiFiles(Array.isArray(remote) ? remote : []);
    } catch (err) {
      console.error("Task file upload:", err);
      setMessage({
        type: "err",
        text: err.response?.data?.message || err.response?.data?.error || err.message || "Upload failed."
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (fileId == null || !Number.isFinite(Number(fileId))) return;
    if (!window.confirm("Delete this attachment? This cannot be undone.")) return;
    setDeletingId(Number(fileId));
    setMessage({ type: "", text: "" });
    try {
      await deleteTaskFile(fileId);
      setMessage({ type: "ok", text: "Attachment deleted." });
      if (onRefresh) await onRefresh();
      const remote = await fetchTaskFiles(resolvedTaskId);
      setApiFiles(Array.isArray(remote) ? remote : []);
    } catch (err) {
      console.error("Task file delete:", err);
      setMessage({
        type: "err",
        text: err.response?.data?.message || err.response?.data?.error || err.message || "Delete failed."
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (resolvedTaskId == null) {
    return (
      <div className="viewlead-section issue-file-attachments-inner">
        <p className="viewlead-no-data">Unable to determine task id for attachments.</p>
      </div>
    );
  }

  return (
    <div className="viewlead-section issue-file-attachments-inner">
      <div className="viewlead-section-header">
        <h3>{title}</h3>
        <div className="viewlead-section-actions">
          {canUpload ? (
            <>
              <input ref={inputRef} type="file" className="issue-files-input-hidden" onChange={handleFileChange} />
              <button
                type="button"
                className="btn-add-attachment"
                disabled={uploading || loading}
                onClick={() => inputRef.current?.click()}
                title="Upload file"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <FaUpload size={14} />
                {uploading ? "Uploading…" : "Upload file"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {message.text ? (
        <div
          className={message.type === "err" ? "issues-alert issues-alert-error" : "issues-alert"}
          style={{ marginBottom: 12 }}
        >
          {message.text}
        </div>
      ) : null}

      {loading ? (
        <p className="viewlead-no-data" style={{ margin: 0 }}>
          Loading attachments…
        </p>
      ) : displayFiles.length > 0 ? (
        <>
        <table className="viewlead-table">
          <thead>
            <tr>
              <th className="issue-attachments-th-sortable" style={getColumnStyle(0)}>
                <div className="issue-attachments-th-sort-row">
                  <SupportSortableHeader
                    columnId="fileName"
                    label="File name"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                </div>
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(0, e); }} />
              </th>
              <th className="issue-attachments-th-sortable" style={getColumnStyle(1)}>
                <div className="issue-attachments-th-sort-row issue-attachments-th-sort-row--center">
                  <SupportSortableHeader
                    columnId="uploadedOn"
                    label="Uploaded at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                </div>
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(1, e); }} />
              </th>
              <th style={getColumnStyle(2)}>
                Open
                <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(2, e); }} />
              </th>
              {allowDelete ? <th className="issue-attachments-actions-col" style={getColumnStyle(3)}>Action</th> : null}
            </tr>
          </thead>
          <tbody>
            {currentFiles.map((f) => {
              const href = fixFileUrl(f.fileUrl);
              return (
                <tr key={String(f.fileId ?? f.fileUrl ?? f.fileName)}>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <FaFileAlt style={{ color: "#3182ce", flexShrink: 0 }} aria-hidden />
                      <span>{f.fileName || "—"}</span>
                    </span>
                  </td>
                  <td className="muted-cell">{formatUploadedOn(f.uploadedOn)}</td>
                  <td>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="viewlead-file-link">
                        View 
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  {allowDelete ? (
                    <td className="issue-attachments-actions-cell">
                      {f.fileId != null ? (
                        <button
                          type="button"
                          className="btn-issue-file-delete"
                          disabled={uploading || loading || deletingId === Number(f.fileId)}
                          onClick={() => handleDelete(f.fileId)}
                          title="Delete attachment"
                          aria-label={`Delete ${f.fileName || "file"}`}
                        >
                          {deletingId === Number(f.fileId) ? "…" : <FaTrash size={14} />}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
        <TablePagination
          currentPage={attachmentPage}
          totalItems={sortedFiles.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setAttachmentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="attachments"
          rowsPerPageSelectId="task-attachments-rows-per-page"
        />
        </>
      ) : (
        <p className="viewlead-no-data">No attachments yet. {canUpload ? 'Click "Upload file" to add one.' : null}</p>
      )}
    </div>
  );
}

